import React, { useRef, useState } from 'react'
import kebabCase from 'lodash.kebabcase'
import clsx from 'clsx'
import { RelatedArticles } from '@/components/molecules/RelatedArticles/RelatedArticles'
import { ArticleSideBar } from '@/components/molecules/ArticleSideBar/ArticleSideBar'
import { RichTextField } from '@/components/organisms/RichTextField/RichTextField'
import { ReleaseSection } from '@/components/organisms/ReleaseSection/ReleaseSection'
import useIntersectionObserver from '@/hooks/useIntersectionObserver'
import { TableOfContent } from '../TableOfContent/TableOfContent'
import * as styles from './ArticleHelpSection.module.css'
import { ArticleHelpProps } from './types'

export function ArticleHelpSection({ content }: ArticleHelpProps) {
  const containerRef = useRef(null)
  const [activeId, setActiveId] = useState('')
  const intersection = useIntersectionObserver(setActiveId)

  const { title, sidebar_accordion, related_articles_title, related_articles, body } = content || {}
  const bodyContent = body.length && body[0]
  const contentMap =
    bodyContent.component === 'section_release' ? (
      <ReleaseSection {...bodyContent} />
    ) : (
      <RichTextField data={bodyContent.content} />
    )

  const tableOfContent = !(bodyContent.component === 'section_release')
    ? bodyContent?.content?.content
        ?.filter((contentEl: { type: string }) => contentEl.type === 'heading')
        .map((heading: { attrs: { level: number }; content: { text: any }[]; level: any }) => {
          const headingIndention = heading.attrs?.level > 1 && `pl-${heading.attrs?.level * 3}`
          const activeAnchor =
            kebabCase(heading.content[0].text) === activeId ? 'activeAnchor font-bold !text-purple' : ''

          return {
            type: 'blok',
            attrs: {
              body: [
                {
                  component: 'component_anchor',
                  id: `#${kebabCase(heading.content[0].text)}`,
                  className: `tableOfContentLink ${headingIndention} ${activeAnchor}`,
                  _uid: 'test',
                  text: heading.content[0].text,
                  level: heading.attrs?.level,
                  attributes: { 'data-level': heading.attrs?.level * 3 }
                }
              ],
              className: heading.level
            }
          }
        })
    : []

  return (
    <section
      className="mb-28 mx-auto px-24 box-border pt-16 laptop:px-90 netbook:pt-64 flex justify-center"
      ref={containerRef}
    >
      <div className="lg:mt-40 lg:pb-80 pb-24 flex relative h-full max-w-90 w-full mx-auto">
        <div
          className={clsx(
            'lg:w-[25%] lg:pr-20 p-0 lg:block hidden pr-20 sticky h-full top-[200px] left-0 translate-x-0 max-w-none',
            styles.sideBar
          )}
        >
          <ArticleSideBar sidebar_accordion={sidebar_accordion?.content} />
        </div>
        <div className="lg:w-[55%] w-full lg:px-40 p-0">
          <div className="block lg:hidden mb-48">
            <TableOfContent content={{ type: 'doc', content: tableOfContent }} defaultOpen={false} />
          </div>
          <article className={styles.articleWrapper} id="contentContainer">
            <h1
              className="font-poppins font-bold text-text-dark-900 text-headingL-mobile tablet:text-headingL mb-24 tablet:mb-16 netbook:mb-64"
              id={kebabCase(title)}
            >
              {title}
            </h1>
            {contentMap}
          </article>
          <RelatedArticles related_articles_title={related_articles_title} related_articles={related_articles} />
        </div>
        <div
          className={clsx(
            'lg:w-[25%] hidden lg:block sticky h-full top-[200px] left-0 translate-x-0 max-w-none',
            styles.sideBar
          )}
        >
          <TableOfContent content={{ type: 'doc', content: tableOfContent }} defaultOpen />
        </div>
      </div>
    </section>
  )
}
